import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { OrderStatus } from "@/types/tms";
import { progressLeg, type ProgressLegResult } from "@/lib/leg-progression";
import { cancelLeg, type CancelLegResult } from "@/lib/leg-cancel";
import { useTenant } from "@/hooks/useTenant";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { ORDER_MUTATION_QUERY_KEYS } from "@/lib/order-cache-keys";

/**
 * Shared write path for moving ONE stop's status forward.
 *
 * `progressLeg` already owns the stop write, the parent rollup and the
 * `order_change_log` rows. This hook adds the React plumbing both surfaces
 * need — pending state, toast, query invalidation — and an optional
 * `onAfterProgress` hook for caller-owned side effects.
 *
 * Two callers:
 *  - `useDriverLegStatusUpdate` (driver app): passes its trailer / yard /
 *    AYMS / VAS / customer-email side effects through `onAfterProgress`.
 *  - `LegStatusDialog` (dispatch): passes none — status and audit only, by
 *    product decision. Dispatch must not move trailers or email customers.
 */
export interface LegStatusProgressionOptions {
  /** Written to `order_change_log.changed_by`. */
  actorLabel: string;
  /** Driver flows guard against skipping in_transit; dispatch does too. */
  requirePriorInTransit?: boolean;
  /** Query keys invalidated after a successful move. */
  invalidateKeys?: readonly (readonly unknown[])[];
  /** Caller-owned side effects, run after the status write succeeds. */
  onAfterProgress?: (ctx: {
    parentOrderId: string;
    legId: string | null;
    newStatus: OrderStatus;
    result: ProgressLegResult;
  }) => Promise<void> | void;
  /** Overrides the success toast text. */
  describeSuccess?: (ctx: { newStatus: OrderStatus; result: ProgressLegResult }) => string;
}

const DEFAULT_KEYS = ORDER_MUTATION_QUERY_KEYS;

export function useLegStatusProgression(options: LegStatusProgressionOptions) {
  const {
    actorLabel,
    requirePriorInTransit = true,
    invalidateKeys = DEFAULT_KEYS,
    onAfterProgress,
    describeSuccess,
  } = options;

  const [isUpdating, setIsUpdating] = useState(false);
  const [pendingLegId, setPendingLegId] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { tenantId } = useTenant();
  const { eapiClient, isReady: eapiReady } = useEapiAuth();

  const progress = async (args: {
    parentOrderId: string;
    legId: string | null;
    newStatus: OrderStatus;
  }): Promise<ProgressLegResult | null> => {
    const { parentOrderId, legId, newStatus } = args;
    setIsUpdating(true);
    setPendingLegId(legId);
    try {
      const result = await progressLeg({
        parentOrderId,
        legId,
        newStatus,
        actorLabel,
        tenantId,
        requirePriorInTransit,
      });

      await onAfterProgress?.({ parentOrderId, legId, newStatus, result });

      toast({
        title: "Status updated",
        description:
          describeSuccess?.({ newStatus, result }) ??
          (result.isMultiLeg && !result.parentPromoted
            ? `Stop marked as ${label(newStatus)} (waiting on other stops)`
            : `Order marked as ${label(newStatus)}`),
      });

      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key as unknown[] });
      }
      return result;
    } catch (error) {
      console.error("[useLegStatusProgression] status update failed", error);
      toast({
        title: "Update failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update the stop status. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUpdating(false);
      setPendingLegId(null);
    }
  };

  /**
   * Cancel a single stop, including its external trip. Separate verb from
   * `progress` — the write path is `cancelLeg`, which is Sync-then-Persist
   * and rolls the parent order up only when the order actually settles.
   */
  const cancel = async (args: {
    parentOrderId: string;
    legId: string;
    reason?: string | null;
    notes?: string | null;
  }): Promise<CancelLegResult | null> => {
    const { parentOrderId, legId, reason = null, notes = null } = args;
    setIsUpdating(true);
    setPendingLegId(legId);
    try {
      const result = await cancelLeg({
        parentOrderId,
        legId,
        reason,
        notes,
        actorLabel,
        tenantId,
        eapiClient,
        eapiReady,
      });

      toast({
        title: "Stop cancelled",
        description:
          result.parentStatus === "canceled"
            ? "Last open stop cancelled — the order is now cancelled."
            : result.parentStatus === "delivered"
              ? "Stop cancelled — the order is now delivered."
              : `Stop${result.legSequence ? ` ${result.legSequence}` : ""} cancelled. The rest of the order is unchanged.`,
      });

      for (const key of invalidateKeys) {
        queryClient.invalidateQueries({ queryKey: key as unknown[] });
      }
      return result;
    } catch (error) {
      console.error("[useLegStatusProgression] leg cancel failed", error);
      toast({
        title: "Cancel failed",
        description:
          error instanceof Error
            ? error.message
            : "Failed to cancel this stop. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsUpdating(false);
      setPendingLegId(null);
    }
  };

  return { progress, cancel, isUpdating, pendingLegId };
}

function label(status: OrderStatus): string {
  return status === "in_transit" ? "In Transit" : status === "delivered" ? "Delivered" : status;
}
